// Services.js
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const serviceItems = [
  {
    title: "Live Streaming",
    description: "High-quality live streaming services for events and programmes",
    bgClass: "",
    image: "/live.jpg",
  },
  {
    title: "Video Production",
    description: "Professional video creation and editing for all occasions",
    bgClass: "alt1",
    image: "/video.jpg",
  },
  {
    title: "Photography",
    description: "Expert event and promotional photography services",
    bgClass: "alt2",
    image: "/photography.jpg",
  },
  {
    title: "Web Design",
    description: "Modern, responsive website design and development",
    bgClass: "alt3",
    image: "/webdesign.jpg",
  },
];

function Services() {
  const sectionRef = useRef(null);
  const headerRef  = useRef(null);
  const cardRefs   = useRef([]);
  const ctaRef     = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const section = sectionRef.current;
    const header  = headerRef.current;
    const cards   = cardRefs.current;
    const cta     = ctaRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            header.classList.add("animate-in");
            cards.forEach((card, i) => {
              setTimeout(() => {
                if (card) card.classList.add("animate-in");
              }, 150 + i * 130);
            });
            // Animate CTA button after all cards
            setTimeout(() => {
              if (cta) cta.classList.add("animate-in");
            }, 150 + cards.length * 130 + 100);
            observer.unobserve(section);
          }
        });
      },
      { threshold: 0.15 }
    );

    if (section) observer.observe(section);
    return () => { if (section) observer.unobserve(section); };
  }, []);

  return (
    <section className="services" id="services" ref={sectionRef}>
      <div className="services-content">

        <div className="section-header" ref={headerRef}>
          <div className="contact-eyebrow">What We Offer</div>
          <h2>Our <span>Services</span></h2>
          <p>Professional media and community engagement solutions tailored to your needs</p>
        </div>

        <div className="services-grid">
          {serviceItems.map((item, index) => (
            <div
              key={index}
              className="service-card"
              ref={(el) => (cardRefs.current[index] = el)}
            >
              {/* Background image layer */}
              <div
                className={`service-bg ${item.bgClass}`}
                style={{
                  backgroundImage: `url(${item.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              ></div>

              {/* Dark gradient overlay so text stays readable */}
              <div style={overlayStyle}></div>

              <div className="service-content">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div ref={ctaRef} style={ctaWrapStyle}>
          <button
            onClick={() => navigate("/welcome")}
            style={ctaButtonStyle}
            onMouseEnter={e => {
              e.currentTarget.style.background = "#2e2e2e";
              e.currentTarget.style.border = "2px solid rgba(255,255,255,0.45)";
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = "0 12px 32px rgba(0,0,0,0.45)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = "#1a1a1a";
              e.currentTarget.style.border = "2px solid rgba(255,255,255,0.25)";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.35)";
            }}
          >
            <span style={ctaIconStyle}>✉</span>
            Request Our Services
            <span style={ctaArrowStyle}>→</span>
          </button>
        </div>

      </div>
    </section>
  );
}

const overlayStyle = {
  position: "absolute",
  inset: 0,
  background: "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0.10) 100%)",
  zIndex: 0,
  pointerEvents: "none",
};

const ctaWrapStyle = {
  display: "flex",
  justifyContent: "center",
  marginTop: "40px",
  opacity: 0,
  transform: "translateY(24px)",
  transition: "opacity 0.6s ease, transform 0.6s ease",
};

// Inject the .animate-in rule via a style tag approach — handled via className toggle
// We attach the animate-in class to trigger the transition above.
// To make this work, add this to your global CSS (main.css):
//   .services .animate-in { opacity: 1 !important; transform: translateY(0) !important; }

const ctaButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "10px",
  background: "#1a1a1a",
  color: "white",
  padding: "14px 34px",
  borderRadius: "12px",
  fontSize: "15px",
  fontWeight: "600",
  letterSpacing: "0.03em",
  textDecoration: "none",
  border: "2px solid rgba(255,255,255,0.25)",
  boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
  transition: "all 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
  cursor: "pointer",
};

const ctaIconStyle = {
  fontSize: "16px",
  lineHeight: 1,
};

const ctaArrowStyle = {
  fontSize: "18px",
  lineHeight: 1,
  transition: "transform 0.25s ease",
};

export default Services;